import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ArrowLeft, Save } from "lucide-react";
import { api } from "@/lib/api";

interface AltaResponse {
  id: string;
}

export function LaboratorioAltaPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    razonSocial: "",
    nombreCorto: "",
    rfc: "",
    acreditacionEma: "",
    acreditacionVigenciaInicio: "",
    acreditacionVigenciaFin: "",
    autorizacionIft: "",
    autorizacionIftFecha: "",
    contactoCorreo: "",
    contactoTelefono: "",
    pais: "MEX",
    notas: "",
  });
  const [error, setError] = useState<string | null>(null);

  const crear = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "") body[k] = v;
      });
      const { data } = await api.post<AltaResponse>("/api/catalogos/laboratorios", body);
      return data;
    },
    onSuccess: (data) => navigate(`/admin/laboratorios/${data.id}`),
    onError: (err: AxiosError<{ detail?: string }>) => {
      setError(err.response?.data?.detail ?? "Error al crear laboratorio");
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.razonSocial.trim()) {
      setError("La razón social es obligatoria");
      return;
    }
    crear.mutate();
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <button
        type="button"
        onClick={() => navigate("/admin/laboratorios")}
        className="inline-flex cursor-pointer items-center gap-1 text-sm text-fg-muted hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Volver
      </button>

      <header>
        <h1 className="text-2xl font-semibold text-fg">Nuevo laboratorio</h1>
        <p className="text-sm text-fg-muted">
          Captura los datos generales del laboratorio. Las normas y alcances se asignan
          desde la página de detalle.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border bg-surface p-4 shadow-card">
        <Field label="Razón social *" hint="Nombre legal completo">
          <input
            type="text"
            required
            value={form.razonSocial}
            onChange={(e) => setForm((f) => ({ ...f, razonSocial: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nombre corto" hint="Etiqueta para UI">
            <input
              type="text"
              value={form.nombreCorto}
              onChange={(e) => setForm((f) => ({ ...f, nombreCorto: e.target.value }))}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </Field>
          <Field label="RFC" hint="13 caracteres, opcional para extranjeros">
            <input
              type="text"
              value={form.rfc}
              maxLength={13}
              onChange={(e) => setForm((f) => ({ ...f, rfc: e.target.value.toUpperCase() }))}
              className="w-full rounded border px-2 py-1.5 text-sm uppercase"
            />
          </Field>
        </div>

        <fieldset className="rounded border p-3">
          <legend className="px-1 text-xs font-medium text-fg-muted">Acreditación EMA</legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Folio">
              <input
                type="text"
                value={form.acreditacionEma}
                onChange={(e) => setForm((f) => ({ ...f, acreditacionEma: e.target.value }))}
                className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              />
            </Field>
            <Field label="Vigencia inicio">
              <input
                type="date"
                value={form.acreditacionVigenciaInicio}
                onChange={(e) => setForm((f) => ({ ...f, acreditacionVigenciaInicio: e.target.value }))}
                className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              />
            </Field>
            <Field label="Vigencia fin">
              <input
                type="date"
                value={form.acreditacionVigenciaFin}
                onChange={(e) => setForm((f) => ({ ...f, acreditacionVigenciaFin: e.target.value }))}
                className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              />
            </Field>
          </div>
        </fieldset>

        <fieldset className="rounded border p-3">
          <legend className="px-1 text-xs font-medium text-fg-muted">Autorización IFT (si aplica)</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Folio">
              <input
                type="text"
                value={form.autorizacionIft}
                onChange={(e) => setForm((f) => ({ ...f, autorizacionIft: e.target.value }))}
                className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              />
            </Field>
            <Field label="Fecha">
              <input
                type="date"
                value={form.autorizacionIftFecha}
                onChange={(e) => setForm((f) => ({ ...f, autorizacionIftFecha: e.target.value }))}
                className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              />
            </Field>
          </div>
        </fieldset>

        <fieldset className="rounded border p-3">
          <legend className="px-1 text-xs font-medium text-fg-muted">Contacto</legend>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Correo">
              <input
                type="email"
                value={form.contactoCorreo}
                onChange={(e) => setForm((f) => ({ ...f, contactoCorreo: e.target.value }))}
                className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              />
            </Field>
            <Field label="Teléfono">
              <input
                type="tel"
                value={form.contactoTelefono}
                onChange={(e) => setForm((f) => ({ ...f, contactoTelefono: e.target.value }))}
                className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
              />
            </Field>
            <Field label="País" hint="ISO-3, default MEX">
              <input
                type="text"
                value={form.pais}
                maxLength={3}
                onChange={(e) => setForm((f) => ({ ...f, pais: e.target.value.toUpperCase() }))}
                className="w-full rounded border px-2 py-1.5 text-sm uppercase"
              />
            </Field>
          </div>
        </fieldset>

        <Field label="Notas">
          <textarea
            rows={3}
            value={form.notas}
            onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        </Field>

        {error && (
          <div className="rounded border border-estado-err/25 bg-estado-err-bg p-2 text-sm text-estado-err">{error}</div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate("/admin/laboratorios")}
            className="cursor-pointer rounded border bg-surface px-3 py-2 text-sm transition hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={crear.isPending}
            className="inline-flex cursor-pointer items-center gap-2 rounded bg-primary px-3 py-2 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> Guardar
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-fg-muted">{label}</span>
      {hint && <span className="ml-2 text-xs text-fg-subtle">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
